import React, { useContext, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Translator } from 'ggApp/modules/translator';
import WaitingListModal from 'ggApp/shared/screens/ProductDetailsPage/Unavailable/WaitingListModal';
import DisplayIf from 'ggApp/shared/components/viewport';
import CarouselV2 from 'ggApp/shared/components/Carousel/v2/Carousel';
import { translate } from 'ggApp/modules/translator/selectors';
import CloseSmall from 'ggApp/shared/icons/CloseSmall';

import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';

import {
    Container,
    Description,
    Button,
    Close,
    CloseButton,
    CloseIconWrap,
    ProductCard,
    Cart,
    CircleLoader,
    ButtonWrap,
} from './styles';

const UnavailableCard = ({
    className,
    item = {},
    onRemove,
    addToWaitingList,
    addNotification,
    translationKeys,
    onSelectSimilarProduct,
    relatedProducts,
    getRelatedProducts,
}) => {
    const { activeStore, user } = useContext(OrderContext);

    const [waitingModal, setWaitingModal] = useState(null);
    const [loadingProduct, setLoadingProduct] = useState(false);
    const [loadingRemoving, setLoadingRemoving] = useState(false);

    const browseSimilarProducts = useCallback(() => {
        if (waitingModal) {
            setLoadingProduct(true);

            getRelatedProducts(item.variant?.product?.slug);
            waitingModal.openModal();
            setLoadingProduct(false);
        }
    }, [waitingModal, item.variant, getRelatedProducts]);

    // to avoid extra components in ProductCard
    const mappedProduct = useMemo(() => {
        return { ...item, quantity: null, selectedPlan: null };
    }, [item]);

    const createModalRefWaiting = useCallback((modal) => {
        setWaitingModal(modal);
    }, []);

    const handleRemove = useCallback(() => {
        if (onRemove && typeof onRemove === 'function') {
            setLoadingRemoving(true);
            onRemove();
            setLoadingRemoving(false);
        }
    }, [onRemove]);

    const onClickCloseIcon = useCallback(() => {
        if (waitingModal) {
            waitingModal.closeModal();
        }
    }, [waitingModal]);

    const processReserve = useCallback(() => {
        addToWaitingList(item.variant.id, user.email, user.first_name, user.last_name).then(() => {
            addNotification({
                id: Date.now(),
                message: translate(
                    translationKeys,
                    activeStore.activeLanguage,
                    'WAITING_LIST_CONFIRMATION_TEXT',
                ),
                level: 'success',
            });
        });
    }, [
        addToWaitingList,
        item,
        user.email,
        user.first_name,
        user.last_name,
        addNotification,
        translationKeys,
        activeStore.activeLanguage,
    ]);

    return (
        <>
            <Container
                className={className}
                data-testid="unavailable-card-id"
                loading={loadingRemoving || loadingProduct}
            >
                <CloseIconWrap onClick={handleRemove}>
                    <Close />
                </CloseIconWrap>

                <ProductCard item={mappedProduct} shouldShowPrice={false} />

                <Description>
                    <Translator tk="CHECKOUT_UNAVAILABLE_BANNER_TEXT" />
                </Description>

                <ButtonWrap>
                    <Button
                        data-testid="browse-similar-products-button"
                        onClick={browseSimilarProducts}
                        isDisabled={loadingProduct || loadingRemoving}
                        small
                        color="blue"
                    >
                        <Translator tk="CHECKOUT_UNAVAILABLE_BANNER_BUTTON" />
                    </Button>

                    {loadingProduct && <CircleLoader />}
                </ButtonWrap>
            </Container>

            <WaitingListModal
                modalRef={createModalRefWaiting}
                currentVariant={item}
                onCtaClick={processReserve}
                onClose={handleRemove}
            >
                {relatedProducts && (
                    <>
                        <CloseButton
                            onClick={onClickCloseIcon}
                            icon={<CloseSmall />}
                            outlined
                            small
                        />
                        <DisplayIf desktop largeDesktop widescreen>
                            <CarouselV2>
                                {relatedProducts &&
                                    relatedProducts.map((similarProduct) => (
                                        <Cart
                                            key={similarProduct.slug}
                                            product={similarProduct}
                                            cardType="vertical"
                                            browserLink
                                            onClick={onSelectSimilarProduct}
                                            carousel
                                        />
                                    ))}
                            </CarouselV2>
                        </DisplayIf>
                        <DisplayIf mobile tablet>
                            {relatedProducts &&
                                relatedProducts.map((similarProduct) => (
                                    <Cart
                                        key={similarProduct.slug}
                                        product={similarProduct}
                                        cardType="horizontal"
                                        browserLink
                                        onClick={onSelectSimilarProduct}
                                    />
                                ))}
                        </DisplayIf>
                    </>
                )}
            </WaitingListModal>
        </>
    );
};

UnavailableCard.propTypes = {
    className: PropTypes.string,
    item: PropTypes.shape({}).isRequired,
    translationKeys: PropTypes.shape({}).isRequired,
    onRemove: PropTypes.func.isRequired,
    onSelectSimilarProduct: PropTypes.func,
    addToWaitingList: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
};
UnavailableCard.defaultProps = {
    className: undefined,
    onSelectSimilarProduct: () => null,
};

export { UnavailableCard };
